/**
 * DP1 · next-action-builders
 *
 * Pure functions that given a data snapshot return the single highest-priority
 * NextAction for a page. Strategy: ordered rule list, first match wins.
 *
 * Each builder is a function:
 *   (snapshot) => NextActionData | null
 *
 * Callers (useNextAction hook) merge the output with page routing.
 * No React imports — purely functional, fully testable.
 */

import { getLocale } from '@/i18n';
import type { NextActionSeverity } from '@/components/ui/NextActionCard';

// ─── Output type ─────────────────────────────────────────────────────────────

export interface NextActionData {
  title: string;
  impact?: string;
  primaryAction: {
    label: string;
    href?: string;
  };
  secondaryActions?: Array<{ label: string; href?: string }>;
  severity: NextActionSeverity;
}

// ─── Snapshot types (minimal, derived from API shapes) ───────────────────────

export interface ProjectsSnapshot {
  projects: Array<{
    id: string;
    name: string;
    status: string;
    rdosLateCount?: number;
  }>;
}

export interface ProjectOverviewSnapshot {
  isEmpty: boolean;
  hasEstimate: boolean;
}

export interface BudgetSnapshot {
  hasSinapiSource: boolean;
  pendingApprovalVersions: number;
  highestUsagePercent: number;    // 0-100+
  highUsageCategory?: string;
}

export interface PurchasesSnapshot {
  quotationsWaitingCount: number;
  quotationsWaitingDays: number;  // max days waiting
}

export interface QualidadeSnapshot {
  overdueFvsCount: number;
  projectsAffected: number;
}

export interface FinancialSnapshot {
  categoryName?: string;
  usagePercent: number;          // 0-100+
}

export interface ScheduleSnapshot {
  nextMilestoneName?: string;
  nextMilestoneDaysLeft?: number;
}

export interface PhotosSnapshot {
  photosWithoutRdoCount: number;
}

export interface TrenaSnapshot {
  oldestDraftAgeDays?: number;    // days since last update of oldest open draft
  openDraftCount: number;
}

export interface InboxSnapshot {
  pendingDecisionsCount: number;
}

// ─── 1. ProjectsPage ─────────────────────────────────────────────────────────

export function buildProjectsNextAction(
  snapshot: ProjectsSnapshot,
): NextActionData | null {
  const isEn = getLocale() === 'en';
  const lateProjects = snapshot.projects.filter(
    (p) => (p.rdosLateCount ?? 0) > 0,
  );
  if (lateProjects.length === 0) return null;

  const totalLate = lateProjects.reduce(
    (acc, p) => acc + (p.rdosLateCount ?? 0),
    0,
  );

  return {
    title:
      isEn
        ? lateProjects.length === 1
          ? `${lateProjects[0]!.name} has a late daily log today`
          : `${lateProjects.length} projects with a late daily log today`
        : lateProjects.length === 1
          ? `${lateProjects[0]!.name} está com RDO atrasado hoje`
          : `${lateProjects.length} obras com RDO atrasado hoje`,
    impact: isEn
      ? `${totalLate} ${totalLate === 1 ? 'daily log' : 'daily logs'} overdue — client may request an update`
      : `${totalLate} ${totalLate === 1 ? 'diário' : 'diários'} em atraso — cliente pode cobrar atualização`,
    primaryAction: {
      label: isEn ? 'Open project' : 'Abrir Marcos',
      href: `/projects/${lateProjects[0]!.id}`,
    },
    severity: 'warn',
  };
}

// ─── 2. ProjectDetailsPage (overview) ────────────────────────────────────────

export function buildProjectOverviewNextAction(
  snapshot: ProjectOverviewSnapshot,
): NextActionData | null {
  if (!snapshot.isEmpty) return null;
  const isEn = getLocale() === 'en';

  return {
    title: isEn ? 'Project created — start with the budget' : 'Obra criada — comece pelo orçamento',
    impact: isEn
      ? 'Trena extracts quantities from the floor plan PDF in minutes'
      : 'Trena extrai quantitativos do PDF da planta em minutos',
    primaryAction: {
      label: isEn ? 'Start with Trena' : 'Começar com Trena',
      href: 'trena',
    },
    severity: 'info',
  };
}

// ─── 3. BudgetPage ───────────────────────────────────────────────────────────

export function buildBudgetNextAction(
  snapshot: BudgetSnapshot,
): NextActionData | null {
  const isEn = getLocale() === 'en';

  // Rule 1: pending approval takes top priority
  if (snapshot.pendingApprovalVersions > 0) {
    return {
      title: isEn
        ? `${snapshot.pendingApprovalVersions} ${snapshot.pendingApprovalVersions === 1 ? 'version' : 'versions'} awaiting approval`
        : `${snapshot.pendingApprovalVersions} ${snapshot.pendingApprovalVersions === 1 ? 'versão' : 'versões'} aguardando aprovação`,
      impact: isEn
        ? 'Approval releases budget for purchasing'
        : 'Aprovação libera comprometimento de verba para compras',
      primaryAction: { label: isEn ? 'Approve pending version' : 'Aprovar versão pendente' },
      severity: 'warn',
    };
  }

  // Rule 2: near 100% usage on any category
  if (snapshot.highestUsagePercent >= 90 && snapshot.highUsageCategory) {
    return {
      title: isEn
        ? `${snapshot.highUsageCategory} budget at ${Math.round(snapshot.highestUsagePercent)}% used`
        : `Verba de ${snapshot.highUsageCategory} em ${Math.round(snapshot.highestUsagePercent)}% usada`,
      impact: isEn
        ? 'Risk of exceeding category before project end'
        : 'Risco de estourar categoria antes do fim da obra',
      primaryAction: { label: isEn ? 'Review items' : 'Revisar itens' },
      severity: snapshot.highestUsagePercent >= 100 ? 'err' : 'warn',
    };
  }

  // Rule 3: no SINAPI source
  if (!snapshot.hasSinapiSource) {
    return {
      title: isEn ? 'Budget has no reference prices' : 'Orçamento sem preço de referência',
      impact: isEn
        ? 'Adding SINAPI reference prices improves traceability and client approval'
        : 'Vincular preços de referência (SINAPI) melhora rastreabilidade e aprovação do cliente',
      primaryAction: { label: isEn ? 'Review references' : 'Revisar referências' },
      severity: 'info',
    };
  }

  return null;
}

// ─── 4. PurchasesPage ────────────────────────────────────────────────────────

export function buildPurchasesNextAction(
  snapshot: PurchasesSnapshot,
): NextActionData | null {
  if (snapshot.quotationsWaitingCount === 0) return null;
  if (snapshot.quotationsWaitingDays < 5) return null;
  const isEn = getLocale() === 'en';

  return {
    title: isEn
      ? `${snapshot.quotationsWaitingCount} ${snapshot.quotationsWaitingCount === 1 ? 'quote has been waiting' : 'quotes have been waiting'} for a response for ${snapshot.quotationsWaitingDays}+ days`
      : `${snapshot.quotationsWaitingCount} ${snapshot.quotationsWaitingCount === 1 ? 'cotação aguarda' : 'cotações aguardam'} resposta há ${snapshot.quotationsWaitingDays}+ dias`,
    impact: isEn
      ? 'Supplier may cancel — deadline approaching'
      : 'Fornecedor pode cancelar proposta — prazo vencendo',
    primaryAction: { label: isEn ? 'Send follow-up' : 'Enviar follow-up' },
    severity: 'warn',
  };
}

// ─── 5. QualidadeHubPage ─────────────────────────────────────────────────────

export function buildQualidadeNextAction(
  snapshot: QualidadeSnapshot,
): NextActionData | null {
  if (snapshot.overdueFvsCount === 0) return null;
  const isEn = getLocale() === 'en';

  return {
    title: isEn
      ? `${snapshot.overdueFvsCount} overdue ${snapshot.overdueFvsCount === 1 ? 'inspection' : 'inspections'} across ${snapshot.projectsAffected} ${snapshot.projectsAffected === 1 ? 'project' : 'projects'}`
      : `${snapshot.overdueFvsCount} ${snapshot.overdueFvsCount === 1 ? 'FVS atrasada' : 'FVS atrasadas'} em ${snapshot.projectsAffected} ${snapshot.projectsAffected === 1 ? 'obra' : 'obras'}`,
    impact: isEn
      ? 'Overdue inspections block the stage and increase rework risk'
      : 'FVS em atraso bloqueia etapa e aumenta risco de retrabalho',
    primaryAction: { label: isEn ? 'Open inspection list' : 'Abrir lista de FVS' },
    severity: snapshot.overdueFvsCount >= 5 ? 'err' : 'warn',
  };
}

// ─── 6. FinancialDepthPage / VerbaPage ───────────────────────────────────────

export function buildFinancialNextAction(
  snapshot: FinancialSnapshot,
): NextActionData | null {
  if (snapshot.usagePercent < 90) return null;
  const isEn = getLocale() === 'en';

  const category = snapshot.categoryName ?? (isEn ? 'category' : 'categoria');
  const pct = Math.round(snapshot.usagePercent);

  return {
    title: isEn ? `${category} budget at ${pct}% used` : `Verba de ${category} em ${pct}% usada`,
    impact: isEn
      ? pct >= 100
        ? 'Category exceeded — urgent review before new purchases'
        : 'Risk of exceeding budget before project end'
      : pct >= 100
        ? 'Categoria estourada — revisão urgente antes de novas compras'
        : 'Risco de estouro antes do fim da obra',
    primaryAction: { label: isEn ? 'Review' : 'Revisar' },
    severity: pct >= 100 ? 'err' : 'warn',
  };
}

// ─── 7. SchedulePage ─────────────────────────────────────────────────────────

export function buildScheduleNextAction(
  snapshot: ScheduleSnapshot,
): NextActionData | null {
  const days = snapshot.nextMilestoneDaysLeft;
  if (days === undefined || days === null) return null;
  if (days > 7) return null;
  const isEn = getLocale() === 'en';

  const milestone = snapshot.nextMilestoneName ?? (isEn ? 'next milestone' : 'próximo marco');
  const urgency = days <= 0 ? 'err' : days <= 3 ? 'warn' : 'info';
  const daysLabel = isEn
    ? days <= 0
      ? 'was due today'
      : days === 1
        ? 'is due tomorrow'
        : `is due in ${days} days`
    : days <= 0
      ? 'venceu hoje'
      : days === 1
        ? 'vence amanhã'
        : `vence em ${days} dias`;

  return {
    title: `"${milestone}" ${daysLabel}`,
    impact: isEn
      ? 'Confirm progress to keep the schedule up to date'
      : 'Confirme o avanço para manter o cronograma atualizado',
    primaryAction: { label: isEn ? 'Confirm progress' : 'Confirmar avanço' },
    severity: urgency,
  };
}

// ─── 8. PhotosPage ───────────────────────────────────────────────────────────

export function buildPhotosNextAction(
  snapshot: PhotosSnapshot,
): NextActionData | null {
  if (snapshot.photosWithoutRdoCount === 0) return null;
  const isEn = getLocale() === 'en';

  return {
    title: isEn
      ? `${snapshot.photosWithoutRdoCount} ${snapshot.photosWithoutRdoCount === 1 ? 'photo without a daily log' : 'photos without a daily log'}`
      : `${snapshot.photosWithoutRdoCount} ${snapshot.photosWithoutRdoCount === 1 ? 'foto sem RDO' : 'fotos sem RDO'} associado`,
    impact: isEn
      ? 'Unlinked photos do not appear in the client report'
      : 'Fotos soltas não aparecem no relatório do cliente',
    primaryAction: { label: isEn ? 'Link to daily log' : 'Vincular ao RDO' },
    severity: 'info',
  };
}

// ─── 9. TrenaListPage ────────────────────────────────────────────────────────

export function buildTrenaNextAction(
  snapshot: TrenaSnapshot,
): NextActionData | null {
  if (snapshot.openDraftCount === 0) return null;

  const days = snapshot.oldestDraftAgeDays ?? 0;
  if (days < 7) return null;
  const isEn = getLocale() === 'en';

  return {
    title: isEn
      ? `Estimate open for ${days} days without being committed`
      : `Estimativa aberta há ${days} dias sem commit`,
    impact: isEn
      ? 'Open estimates do not feed the project budget'
      : 'Estimativas abertas não alimentam o orçamento da obra',
    primaryAction: { label: isEn ? 'Continue estimate' : 'Continuar estimativa' },
    severity: days >= 14 ? 'warn' : 'info',
  };
}

// ─── 10. InboxPage ───────────────────────────────────────────────────────────

export function buildInboxNextAction(
  snapshot: InboxSnapshot,
): NextActionData | null {
  if (snapshot.pendingDecisionsCount === 0) return null;
  const isEn = getLocale() === 'en';

  return {
    title: isEn
      ? `${snapshot.pendingDecisionsCount} ${snapshot.pendingDecisionsCount === 1 ? 'pending decision' : 'pending decisions'}`
      : `${snapshot.pendingDecisionsCount} ${snapshot.pendingDecisionsCount === 1 ? 'decisão pendente' : 'decisões pendentes'}`,
    impact: isEn
      ? 'Pending decisions can hold up purchases and payments'
      : 'Decisões pendentes podem travar compras e pagamentos',
    primaryAction: { label: isEn ? 'Review next' : 'Revisar próxima' },
    severity: snapshot.pendingDecisionsCount >= 5 ? 'warn' : 'info',
  };
}
